import { ChangeEvent, ReactNode, useCallback } from 'react';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import classNames from 'classnames';

import { Box } from '../../Box';
import { CircularProgress } from '../../CircularProgress';
import { Label } from '../common';
import { Typography } from '../../Typography';

import { ChipsInputItem } from './ChipsInput.types';
import { useStyles } from './ChipsInput.styles';

/**
 * Свойства компонента {@link ChipsInput}
 */
interface ChipsInputProps {
  /**
   * Массив элементов для отображения в виде чипов.
   */
  items: ChipsInputItem[];

  /**
   * Текст метки.
   */
  label?: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Текущее выбранное значение.
   */
  value: string | number | undefined;

  /**
   * Обработчик изменения значения.
   */
  onChange?: (value: string | number) => void;

  /**
   * Обработчик нажатия на чип.
   */
  onClick?: (
    e: React.KeyboardEvent<Element> | React.MouseEvent<Element, MouseEvent>,
  ) => void;

  /**
   * Флаг, указывающий на обязательность поля.
   */
  required?: boolean;

  /**
   * Флаг, указывающий на наличие ошибки.
   */
  error?: boolean;

  /**
   * Вспомогательный текст, отображаемый при ошибке.
   */
  helperText?: string;

  /**
   * Вспомогательный текст или узел, отображаемый в виде подсказки.
   */
  tooltip?: ReactNode | null;

  /**
   * Флаг, указывающий на отключенность компонента.
   */
  disabled?: boolean;

  /**
   * Флаг, указывающий на состояние загрузки.
   */
  loading?: boolean;

  /**
   * Размер чипов.
   */
  size?: 'large' | 'medium' | 'small' | 'extraSmall';

  /**
   * Направление расположения чипов.
   */
  direction?: 'row' | 'column';

  /**
   * Флаг, указывающий на активность единственной опции.
   */
  activeSingleOption?: boolean;

  /**
   * Ширина лейбла чипа.
   */
  chipLabelWidth?: number;

  /**
   * Идентификатор для тестирования.
   */
  testId?: string;
  variant?: 'default' | 'outlined';
}

/**
 * ChipsInput - компонент для выбора значения с помощью чипов.
 *
 * @public
 * @param items - Массив элементов для отображения в виде чипов.
 * @param label - Текст метки.
 * @param labelIcon - Иконка для метки.
 * @param value - Текущее выбранное значение.
 * @param onChange - Обработчик изменения значения.
 * @param onClick - Обработчик нажатия на чип.
 * @param required - Флаг, указывающий на обязательность поля.
 * @param error - Флаг, указывающий на наличие ошибки.
 * @param helperText - Вспомогательный текст, отображаемый при ошибке.
 * @param tooltip - Вспомогательный текст или узел, отображаемый в виде подсказки.
 * @param disabled - Флаг, указывающий на отключенность компонента.
 * @param loading - Флаг, указывающий на состояние загрузки.
 * @param size - Размер чипов.
 * @param direction - Направление расположения чипов.
 * @param activeSingleOption - Флаг, указывающий на активность единственной опции.
 * @param chipLabelWidth - Ширина лейбла чипа.
 * @param testId - Идентификатор для тестирования.
 * @param variant - Вариант оформления чипов.
 * @returns Возвращает JSX-элемент представляющий компонент ChipsInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ChipsInputProps}
 */
export const ChipsInput = ({
  label,
  labelIcon,
  items,
  value,
  onChange,
  onClick,
  required = false,
  error,
  helperText,
  disabled = false,
  loading = false,
  tooltip = null,
  size = 'large',
  direction = 'row',
  activeSingleOption = true,
  testId,
  variant = 'default',
  chipLabelWidth,
  ...rest
}: ChipsInputProps) => {
  const { classes } = useStyles();

  const handleChange = useCallback(
    ({ target: { value: inputValue } }: ChangeEvent<HTMLInputElement>) => {
      onChange?.(inputValue);
    },
    [onChange],
  );

  const handleClick = (
    e: React.KeyboardEvent<Element> | React.MouseEvent<Element, MouseEvent>,
  ) => {
    onClick?.(e);
  };

  return (
    <FormControl
      className={`${classes.root} aqa_chips_input`}
      component="fieldset"
    >
      {label && (
        <Label
          label={label}
          className="aqa_chips_input_label"
          required={required}
          tooltip={tooltip}
          icon={labelIcon}
        />
      )}

      <RadioGroup
        row={direction === 'row'}
        data-testid={testId}
        value={value}
        onChange={(event) => {
          handleChange(event);
        }}
        className={`${classes.row} aqa_chip_button`}
      >
        {items.map(
          ({ label: itemLabel, value: itemValue, counter, color, icon }) => {
            const active =
              itemValue === value || (activeSingleOption && items.length === 1);

            return (
              <Box key={itemValue} className={classes.wrapper}>
                <FormControlLabel
                  key={itemValue}
                  value={itemValue}
                  className={classes.label}
                  disabled={disabled}
                  control={<Radio className={classes.radio} />}
                  label={
                    <Chip
                      {...rest}
                      onClick={handleClick}
                      label={
                        loading ? (
                          <CircularProgress
                            size={20}
                            color={itemValue === value ? 'primary' : 'inherit'}
                          />
                        ) : (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            gap="8"
                            width={chipLabelWidth}
                            minWidth={0}
                            ml={icon && itemLabel ? '2' : '0'}
                          >
                            <Typography
                              variant="inherit"
                              color="inherit"
                              noWrap
                            >
                              {itemLabel}
                            </Typography>

                            {!!counter && (
                              <Box className={classes.counter}>{counter}</Box>
                            )}
                          </Box>
                        )
                      }
                      variant="outlined"
                      disabled={disabled}
                      clickable={!disabled}
                      icon={!loading && icon ? <Box>{icon}</Box> : undefined}
                      className={classNames(
                        classes.chip,
                        classes[size],
                        active && classes.active,
                        !active && variant === 'outlined' && classes.outlined,
                        error && classes.error,
                        !active && variant === 'outlined' && classes.outlined,
                        disabled && classes.disabled,
                        active && disabled && classes.activeDisabled,
                        color && classes[color],
                      )}
                    />
                  }
                />
              </Box>
            );
          },
        )}
      </RadioGroup>

      {error && (
        <Box mt="4">
          <Typography variant="caption" color="accent.text.error">
            {helperText}
          </Typography>
        </Box>
      )}
    </FormControl>
  );
};
